import AppModal from "components/Modal";
import { keychainCreateProposal } from "helpers/keychain";
import { createProposalWithWif } from "helpers/steem";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "helpers/authContext";
import { isLoggedWithKeychain } from "services/dsteem-service";
import useModal from "helpers/useModal";
import LoginModal from "features/user/userModals/LoginModal";
import { useToast } from "components/ui/use-toast";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Card,
  CardBody
} from "reactstrap";

type FormData = {
  username: string,
  receiver: string,
  subject: string,
  permlink: string,
  daily_pay: number,
  start: Date,
  end: Date
};

type Props = {
  isShowing: boolean,
  hide: any
}

export const SubmitProposalModal = ({ isShowing, hide }: Props) => {
  const { register, handleSubmit, reset } = useForm<FormData>();
  const { user, autologin } = useAuth();
  const { toast } = useToast();
  const { isOpenLoginModal, toggleLoginModal } = useModal();
  
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requiredActive, setRequiredActive] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);

  const keychainLogin = isLoggedWithKeychain(autologin);

  const handleCreateProposal = async (wif = "") => {
    if (!formData) return;

    setIsSubmitting(true);
    try {
      if (keychainLogin) {
        // Keychain logic
        // Note: keychainCreateProposal doesn't return a promise in the helper, 
        // but we treat it as an operation. In a real scenario we'd want a callback.
        keychainCreateProposal(
          formData.username, 
          formData.receiver, 
          formData.subject, 
          formData.permlink, 
          formData.daily_pay, 
          formData.start, 
          formData.end
        );
        toast({ title: "Success", description: "Proposal creation requested via Keychain.", variant: "wild_green" });
        hide();
      } else {
        // WIF logic
        const res: any = await createProposalWithWif(
          autologin,
          formData.receiver,
          formData.subject,
          formData.permlink,
          formData.daily_pay,
          formData.start,
          formData.end,
          wif
        );

        if (res.success) {
          toast({ title: "Success", description: "Proposal created successfully!", variant: "wild_green" });
          hide();
        } else {
          if (res?.message?.toString().includes("Missing Active Authority")) {
            setRequiredActive(true);
            toggleLoginModal();
          } else {
            toast({ title: "Error", description: res.message || "Failed to create proposal.", variant: "rose_red" });
          }
        }
      }
    } catch (error) {
      toast({ title: "Error", description: "An unexpected error occurred.", variant: "rose_red" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = (data: FormData) => {
    setFormData(data);
    setStep(2);
  };

  const handleReset = () => {
    reset();
    setStep(1);
    setFormData(null);
  };

  const renderStep1 = (
    <Form className="post-editor-wrapper">
      <div className="post-editor-scroll-area px-2">
        <FormGroup>
          <Label className="form-control-label"><i className="fas fa-heading mr-2" />Proposal Title</Label>
          <input
            id="subject"
            className="form-control fancy-input"
            name="subject"
            placeholder="Enter a short, descriptive title"
            type="text"
            required
            {...register("subject")}
          />
        </FormGroup>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label className="form-control-label"><i className="fas fa-link mr-2" />Permlink</Label>
              <input
                id="permlink"
                className="form-control fancy-input"
                name="permlink"
                placeholder="proposal-permlink"
                type="text"
                required
                {...register("permlink")}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label className="form-control-label"><i className="fas fa-money-bill-wave mr-2" />Daily Payout (SBD)</Label>
              <input
                id="daily_pay"
                className="form-control fancy-input"
                name="daily_pay"
                type="number"
                placeholder="0.000"
                required
                {...register("daily_pay")}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label className="form-control-label"><i className="fas fa-user mr-2" />Creator Account</Label>
              <input
                id="username"
                className="form-control fancy-input"
                name="username"
                placeholder="account name"
                type="text"
                defaultValue={user || ""}
                required
                {...register("username")}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label className="form-control-label"><i className="fas fa-hand-holding-usd mr-2" />Receiver Account</Label>
              <input
                id="receiver"
                className="form-control fancy-input"
                name="receiver"
                placeholder="account name"
                type="text"
                required
                {...register("receiver")}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label className="form-control-label"><i className="fas fa-calendar-alt mr-2" />Start Date</Label>
              <input
                id="start"
                className="form-control fancy-input"
                name="start"
                type="date"
                required
                {...register("start")}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label className="form-control-label"><i className="fas fa-calendar-check mr-2" />End Date</Label>
              <input
                id="end"
                className="form-control fancy-input"
                name="end"
                type="date"
                required
                {...register("end")}
              />
            </FormGroup>
          </Col>
        </Row>
      </div>

      <div className="post-editor-buttons px-2">
        <div className="d-flex gap-2">
          <Button color="secondary" onClick={hide}>
            <i className="fas fa-times mr-2" /> Cancel
          </Button>
          <Button color="secondary" onClick={handleReset}>
            <i className="fas fa-trash mr-2" /> Clear
          </Button>
        </div>
        <Button
          color="default"
          type="button"
          className="btn-fancy"
          onClick={handleSubmit(handleNext)}
        >
          Next <i className="fas fa-chevron-right ml-2" />
        </Button>
      </div>
    </Form>
  );

  const renderStep2 = (
    <div className="post-editor-wrapper">
      <div className="post-editor-scroll-area px-2 py-3">
        <h4 className="mb-3 text-muted">Confirm Proposal Details</h4>
        <div className="proposal-summary p-3 rounded" style={{ backgroundColor: "rgba(0,0,0,0.05)" }}>
          <p><strong>Title:</strong> {formData?.subject}</p>
          <p><strong>Receiver:</strong> {formData?.receiver}</p>
          <p><strong>Daily Pay:</strong> {formData?.daily_pay} SBD</p>
          <p><strong>Duration:</strong> {formData?.start?.toString()} to {formData?.end?.toString()}</p>
          <p><strong>Permlink:</strong> {formData?.permlink}</p>
        </div>
        <p className="mt-4 small text-muted">
          <i className="fas fa-info-circle mr-2" />
          This operation requires your <strong>Active Key</strong>.
        </p>
      </div>

      <div className="post-editor-buttons px-2">
        <Button color="secondary" onClick={() => setStep(1)}>
          <i className="fas fa-chevron-left mr-2" /> Back
        </Button>
        <Button
          color="default"
          type="button"
          className="btn-fancy"
          disabled={isSubmitting}
          onClick={() => handleCreateProposal()}
        >
          {isSubmitting ? (
            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
          ) : keychainLogin ? (
            <>Submit with <img className="icon icon-sm ml-1" src="/assets/img/keychain.svg" alt="Keychain" /></>
          ) : (
            <>Confirm & Publish</>
          )}
        </Button>
      </div>
    </div>
  );

  const renderContent = (
    <div className="modal-body post-editor-body mt-0 p-0">
      <Card className="shadow border-0">
        <CardBody className="px-lg-3 pt-0 pb-0 body-gray">
          {step === 1 ? renderStep1 : renderStep2}
        </CardBody>
      </Card>
    </div>
  );

  return (
    isShowing ? ReactDOM.createPortal(
      <React.Fragment>
        <AppModal
          isShowing={isShowing}
          hide={hide}
          size="lg"
          header={step === 1 ? "Submit a proposal" : "Confirm Publication"}
          children={renderContent}
        />
        <LoginModal
          isShowing={isOpenLoginModal}
          hide={toggleLoginModal}
          requiredActive={requiredActive}
          onSuccess={({ wif }) => {
            if (wif) {
              handleCreateProposal(wif);
            }
          }}
        />
      </React.Fragment>, document.body
    ) : null)
};

export default SubmitProposalModal;